// src/features/whatsapp/pages/GoogleReviewsPage.tsx — 1-oct-2026
// ============================================================================
// «Reseñas en Google»: a un cliente FRECUENTE que calificó su visita en el kiosko le llega
// UN WhatsApp, unas horas después, con el enlace directo para escribir su reseña en Google.
// Una sola vez por cliente, para siempre. El envío y las reglas viven en lealify-base
// (lib/googleReviewAsks.js); aquí se enciende/apaga, se ajusta y se ven los números.
// Lo que solo cambia Lealify: el perfil de Google ligado y el modo prueba/en vivo.
// 🚨 Sin puntos ni premios a cambio de la reseña: Google lo prohíbe y castiga el perfil.
// ============================================================================
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, ExternalLink, MousePointerClick, Send, FlaskConical, RotateCcw } from 'lucide-react';
import Layout from '../../../components/layout/Layout';
import { useAuth } from '../../../context/AuthContext';
import { ProLocked } from '../../../components/pro/ProLocked';
import { isPro } from '../../../utils/planPermissions';
import Toggle from '../../../components/ui/Toggle';
import AlertBox from '../../../components/ui/AlertBox';
import BrandLoader from '../../../components/common/BrandLoader';
import WhatsAppPreviewBubble from '../components/WhatsAppPreviewBubble';
import VolverAMensajes from '../components/VolverAMensajes';
import PaginationControls from '../../../components/common/PaginationControls';
import { googleReviewsApi, type GRSummary, type GRConfig, type GRRating, type GRRow } from '../../../api/googleReviewsApi';

const AFTER = [2, 3, 4, 5];
const DELAYS = [60, 120, 180];
const PER_PAGE = 20;   // paginado en el servidor, como Últimas visitas
// Texto editable (2-oct-2026, pedido de Kevin): mismas variables @ que bienvenida/visita.
const VARS = [
  { key: '@nombre', label: 'Primer nombre del cliente', example: 'Ana' },
  { key: '@marca', label: 'Nombre de tu negocio', example: '' },
  { key: '@enlace', label: 'Enlace directo a escribir la reseña (obligatorio)', example: 'enlace a Google' },
];
const vistaPrevia = (tpl: string, marca: string) => tpl
  .replace(/@nombre/g, 'Ana').replace(/@marca/g, marca).replace(/@enlace/g, '(enlace directo a escribir tu reseña)')
  .replace(/ +([!?,.])/g, '$1');
const horas = (m: number) => `${m / 60} ${m === 60 ? 'hora' : 'horas'}`;
const ESTADO: Record<string, string> = { sent: 'Enviado', shadow: 'En prueba (no se envió)', failed: 'No se pudo enviar' };

const SELECT = 'rounded-md border border-line bg-surface text-content text-sm py-1.5 pl-2 pr-8';
const ROW = 'px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3';

const GoogleReviewsPageContent: React.FC = () => {
  const [d, setD] = useState<GRSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState<string | null>(null);
  const navigate = useNavigate();
  // Lista paginada: la página 1 sin filtro viene con el resumen; el resto se pide aparte.
  const [rows, setRows] = useState<GRRow[]>([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [soloClic, setSoloClic] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);

  useEffect(() => {
    googleReviewsApi.summary().then((s) => {
      setD(s); setRows(s.rows); setRowsTotal(s.rowsTotal ?? s.rows.length);
    }).catch((e) => setError(e?.message || 'No se pudo cargar'));
  }, []);

  const cargarLista = async (p: number, clic: boolean) => {
    setRowsLoading(true);
    try {
      const r = await googleReviewsApi.asks({ page: p, size: PER_PAGE, clicked: clic });
      setRows(r.rows); setRowsTotal(r.total); setPage(p); setSoloClic(clic);
    } catch (e: any) { setError(e?.message || 'No se pudo cargar la lista'); }
    finally { setRowsLoading(false); }
  };

  const [texto, setTexto] = useState<string | null>(null);   // borrador; null = sin tocar
  const guardar = async (patch: Partial<Pick<GRConfig, 'enabled' | 'afterVisits' | 'delayMin' | 'ratings' | 'template'>>) => {
    if (!d) return;
    setBusy(true); setError(null); setOk(null);
    try {
      const r = await googleReviewsApi.save(patch);
      setD({ ...d, config: r.config });
      if ('template' in patch) setTexto(null);
      setOk('Guardado');
      setTimeout(() => setOk(null), 2000);
    } catch (e: any) { setError(e?.message || 'No se pudo guardar'); }
    finally { setBusy(false); }
  };

  const titulo = (
    <div className="animate-fade-in-up">
      <VolverAMensajes />
      <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
        <Star className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-amber-500" />Reseñas en Google
      </h1>
      <p className="mt-1 text-sm text-content-2">Pídele a tus clientes frecuentes que te dejen su reseña en Google, una sola vez y en el mejor momento</p>
    </div>
  );

  if (!d) {
    return (
      <Layout>
        <div className="w-full space-y-6">
          {titulo}
          {error ? <AlertBox variant="error">{error}</AlertBox> : <div className="flex justify-center py-16"><BrandLoader /></div>}
        </div>
      </Layout>
    );
  }

  const c = d.config;
  const g = d.google;
  const nuevas = g && g.first.total != null && g.last.total != null ? g.last.total - g.first.total : null;
  const soloExcelente = c.ratings.length === 1;
  const sinWa = !!d.whatsapp && !d.whatsapp.connected;

  return (
    <Layout>
      <div className="w-full space-y-6">
        {titulo}
        {error && <AlertBox variant="error">{error}</AlertBox>}
        {ok && <AlertBox variant="success">{ok}</AlertBox>}
        {!c.hasPlace && (
          <AlertBox variant="info" title="Falta ligar tu perfil de Google">
            Escríbenos y lo dejamos listo: necesitamos saber cuál es tu negocio en Google Maps.
          </AlertBox>
        )}
        {c.enabled && d.whatsapp && !d.whatsapp.connected && (
          <AlertBox variant="warning" title="WhatsApp no está conectado">
            Mientras no lo conectes no sale ningún mensaje. Cuando lo conectes, salen los pendientes de las últimas 36 horas.{' '}
            <button onClick={() => navigate('/dashboard/whatsapp')} className="font-medium underline hover:opacity-80">Conectar WhatsApp</button>
          </AlertBox>
        )}
        {c.enabled && c.mode !== 'live' && (
          <AlertBox variant="warning" title="En prueba">
            Todavía no se envía nada: revisamos los primeros casos contigo antes de activarlo.
          </AlertBox>
        )}

        {/* Números */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 animate-fade-in-up">
          <div className="bg-surface shadow rounded-lg p-4">
            <p className="text-xs text-content-3">Reseñas en Google</p>
            <p className="text-2xl font-bold text-content tabular-nums">{g?.last.total ?? '—'}</p>
            {/* Fecha de la última revisión desde el dato (Carlos 1-nov-2026): el conteo corre cada mañana a las 7:15. */}
            <p className="text-xs text-content-3">{g?.last.rating ? `${g.last.rating}★ promedio · revisado ${g.last.day.slice(5).split('-').reverse().join('/')}` : 'sin datos aún'}</p>
          </div>
          <div className="bg-surface shadow rounded-lg p-4">
            <p className="text-xs text-content-3">Nuevas desde {g ? g.first.day.slice(5).split('-').reverse().join('/') : '—'}</p>
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">{nuevas == null ? '—' : `+${Math.max(0, nuevas)}`}</p>
            <p className="text-xs text-content-3">{g && g.first.total != null ? `empezamos con ${g.first.total}` : 'conteo diario de Google'}</p>
          </div>
          <div className="bg-surface shadow rounded-lg p-4">
            <p className="text-xs text-content-3 flex items-center"><Send className="h-3 w-3 mr-1" />Mensajes enviados</p>
            <p className="text-2xl font-bold text-content tabular-nums">{d.totals.enviadas}</p>
            <p className="text-xs text-content-3">{d.totals.sombra && c.mode !== 'live' ? `${d.totals.sombra} en prueba` : 'uno por cliente'}</p>
          </div>
          <div className="bg-surface shadow rounded-lg p-4">
            <p className="text-xs text-content-3 flex items-center"><MousePointerClick className="h-3 w-3 mr-1" />Fueron a Google</p>
            <p className="text-2xl font-bold text-content tabular-nums">{d.totals.clics}</p>
            <p className="text-xs text-content-3">abrieron el enlace</p>
          </div>
        </div>

        {/* Ajustes */}
        <div className="bg-surface shadow rounded-lg overflow-hidden divide-y divide-line animate-fade-in-up">
          <div className={ROW}>
            <div>
              <p className="text-sm font-medium text-content">Pedir reseña en Google</p>
              <p className="text-xs text-content-3">Le llega un solo WhatsApp en toda su vida como cliente, desde tu número.</p>
              {!c.enabled && sinWa && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  Conecta tu WhatsApp para poder encenderlo.{' '}
                  <button onClick={() => navigate('/dashboard/whatsapp')} className="font-medium underline hover:opacity-80">Conectar WhatsApp</button>
                </p>
              )}
            </div>
            <Toggle checked={c.enabled} disabled={busy || (!c.enabled && (!c.hasPlace || sinWa))} onChange={(v) => guardar({ enabled: v })} label="Pedir reseña en Google" />
          </div>
          {c.enabled && (
            <>
              <div className={ROW}>
                <div>
                  <p className="text-sm font-medium text-content">¿A partir de qué visita?</p>
                  <p className="text-xs text-content-3">Se le pide a quien ya es cliente frecuente, no en su primera vez.</p>
                </div>
                <select className={SELECT} value={c.afterVisits} disabled={busy} onChange={(e) => guardar({ afterVisits: Number(e.target.value) })}>
                  {AFTER.map((n) => <option key={n} value={n}>{n}.ª visita</option>)}
                </select>
              </div>
              <div className={ROW}>
                <div>
                  <p className="text-sm font-medium text-content">¿Cómo calificó esa visita?</p>
                  <p className="text-xs text-content-3">Lo que el cliente tocó en el kiosko. Si calificó menos, se espera a una visita siguiente.</p>
                </div>
                <select className={SELECT} value={soloExcelente ? 'e' : 'eb'} disabled={busy}
                  onChange={(e) => guardar({ ratings: (e.target.value === 'e' ? ['excelente'] : ['excelente', 'bueno']) as GRRating[] })}>
                  <option value="e">Solo «Excelente»</option>
                  <option value="eb">«Excelente» o «Bueno»</option>
                </select>
              </div>
              <div className={ROW}>
                <div>
                  <p className="text-sm font-medium text-content">¿Cuánto después de la visita?</p>
                  <p className="text-xs text-content-3">Solo sale entre 9:00 y 21:00.</p>
                </div>
                <select className={SELECT} value={c.delayMin} disabled={busy} onChange={(e) => guardar({ delayMin: Number(e.target.value) })}>
                  {(DELAYS.includes(c.delayMin) ? DELAYS : [c.delayMin, ...DELAYS]).map((m) => <option key={m} value={m}>{horas(m)}</option>)}
                </select>
              </div>
            </>
          )}
        </div>

        {/* Así le llega — editable por negocio */}
        {(() => {
          const actual = c.template ?? c.defaultTemplate;
          const valor = texto ?? actual;
          const marca = c.brandName || 'tu negocio';
          const cambiado = valor.trim() !== actual.trim();
          const sinEnlace = !valor.includes('@enlace');
          const largo = valor.length > 1000;
          const puede = d.your_level === 'manage';
          return (
            <div className="bg-surface shadow rounded-lg p-4 sm:p-6 space-y-3 animate-fade-in-up">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-content">Así le llega a tu cliente</p>
                {puede && (c.template || cambiado) && (
                  <button onClick={() => setTexto(c.defaultTemplate)} disabled={busy} className="inline-flex items-center text-xs text-content-3 hover:text-content-2" title="Volver al texto de Lealify">
                    <RotateCcw className="h-3 w-3 mr-1" /> Restaurar
                  </button>
                )}
              </div>
              <WhatsAppPreviewBubble message={vistaPrevia(valor, marca)} />
              {puede && (
                <>
                  <textarea
                    rows={7}
                    value={valor}
                    onChange={(e) => setTexto(e.target.value)}
                    disabled={busy}
                    className="block w-full rounded-md border-line bg-surface text-content shadow-sm sm:text-sm resize-y"
                  />
                  <div className="flex flex-wrap gap-2">
                    {VARS.map((v) => (
                      <button key={v.key} type="button" onClick={() => setTexto(`${valor}${valor.endsWith(' ') || !valor ? '' : ' '}${v.key}`)}
                        title={v.label}
                        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-100">
                        <code className="mr-1">{v.key}</code><span className="opacity-70">→ {v.key === '@marca' ? marca : v.example}</span>
                      </button>
                    ))}
                  </div>
                  {sinEnlace && <p className="text-xs text-red-600 dark:text-red-400">Falta @enlace: es por donde tu cliente llega a Google.</p>}
                  {largo && <p className="text-xs text-red-600 dark:text-red-400">Máximo 1,000 caracteres ({valor.length}).</p>}
                  {cambiado && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => guardar({ template: valor.trim() === c.defaultTemplate.trim() ? null : valor })} disabled={busy || sinEnlace || largo}
                        className="px-4 py-2 rounded-md bg-accent-solid text-white text-sm font-medium hover:opacity-90 disabled:opacity-50">Guardar mensaje</button>
                      <button onClick={() => setTexto(null)} disabled={busy} className="px-3 py-2 text-sm text-content-2 hover:text-content">Cancelar</button>
                    </div>
                  )}
                </>
              )}
              {c.googleUrl && (
                <a href={c.googleUrl} target="_blank" rel="noreferrer" className="inline-flex items-center text-xs text-accent hover:underline">
                  Ver a dónde lleva el enlace <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              )}
              <p className="text-xs text-content-3">No ofrezcas puntos ni premios a cambio de la reseña: Google lo prohíbe y puede castigar tu perfil.</p>
            </div>
          );
        })()}

        {/* Últimas */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up">
          <div className="px-4 sm:px-6 py-4 border-b border-line flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-content">Clientes a los que se les pidió{rowsTotal ? ` (${rowsTotal})` : ''}</p>
            <label className="inline-flex items-center gap-2 text-xs text-content-2 cursor-pointer select-none">
              <input type="checkbox" className="rounded border-line" checked={soloClic} disabled={rowsLoading}
                onChange={(e) => cargarLista(1, e.target.checked)} />
              Solo los que fueron a Google
            </label>
          </div>
          {rows.length === 0 ? (
            <p className="px-4 sm:px-6 py-6 text-sm text-content-3">
              {soloClic ? 'Nadie ha abierto el enlace todavía.' : 'Todavía no hay ninguno. Aparecerán aquí cuando un cliente frecuente califique su visita.'}
            </p>
          ) : (
            <ul className={`divide-y divide-line ${rowsLoading ? 'opacity-50' : ''}`}>
              {rows.map((r) => (
                <li key={r.id} className="px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm text-content truncate">{r.name}</p>
                    <p className="text-xs text-content-3">{r.at} · calificó «{r.rating || '—'}»{r.detail ? ` · ${r.detail}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status === 'shadow' && <span className="inline-flex items-center text-xs text-amber-700 dark:text-amber-300"><FlaskConical className="h-3 w-3 mr-1" />{ESTADO.shadow}</span>}
                    {r.status !== 'shadow' && <span className="text-xs text-content-2">{ESTADO[r.status] || r.status}</span>}
                    {r.clickedAt && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">Fue a Google</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {rowsTotal > PER_PAGE && (
            <PaginationControls
              currentPage={page}
              isLoading={rowsLoading}
              onPrevPage={() => cargarLista(page - 1, soloClic)}
              onNextPage={() => cargarLista(page + 1, soloClic)}
              canGoPrev={page > 1}
              canGoNext={page * PER_PAGE < rowsTotal}
              itemsPerPage={PER_PAGE}
              countOnPage={rows.length}
              totalItems={rowsTotal}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

// Bloqueo PRO (2-oct-2026, plan pantallas PRO F4): un gratis veía la pantalla completa y podía editar el
// mensaje, pero sin WhatsApp propio nunca sale nada. Solo Snowtea y Marrieto (PRO) la tienen configurada.
const GoogleReviewsPage: React.FC = () => {
  const { currentUser } = useAuth();
  if (isPro(currentUser?.subscription)) return <GoogleReviewsPageContent />;
  return (
    <Layout>
      <div className="space-y-6">
        <div className="animate-fade-in-up">
          <VolverAMensajes />
          <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
            <Star className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-amber-500" />Reseñas en Google
          </h1>
          <p className="mt-1 text-sm text-content-2">Pídele a tus clientes frecuentes que te dejen su reseña en Google, una sola vez y en el mejor momento</p>
        </div>
        <ProLocked feature="google-reviews" />
      </div>
    </Layout>
  );
};

export default GoogleReviewsPage;
